import { useQuery } from '@tanstack/react-query';
import { Autocomplete } from 'src/components/Autocomplete';
import { TextField } from 'src/components/TextField';
import api from 'src/utils/ajax';

interface Assessment {
  id: string;
  name: string;
}

interface AssessmentFilterProps {
  label: string;
  value: string | undefined;
  setValue: (newVal: string) => void;
  valueOnly?: boolean;
  multiple?: boolean;
}

export const AssessmentFilter: React.FC<AssessmentFilterProps> = ({
  label,
  value,
  setValue,
  valueOnly,
  multiple = false,
}) => {
  const { data: assessments = [] } = useQuery({
    queryKey: ['assessments'],

    queryFn: async () => {
      const response = await api.get('/api/v2/assessments', {
        params: {
          fetch_all: true,
          pluck_columns: 'id,name',
          'q[s]': 'name asc',
        },
      });
      return response.data;
    },
  });

  const assessmentIds = value ? String(value).split(',').filter(Boolean) : [];

  if (valueOnly) {
    if (multiple) {
      return (
        <>
          {assessmentIds
            ?.map((v) => assessments?.find((a) => String(a.id) === v)?.name)
            ?.filter(Boolean)
            ?.join(', ')}
        </>
      );
    }
    const selectedAssessment = assessments.find(
      (a) => String(a.id) === assessmentIds[0]
    );
    return <>{selectedAssessment?.name || ''}</>;
  }

  return (
    <Autocomplete
      multiple={multiple}
      value={
        multiple
          ? assessments?.filter((a) => assessmentIds?.includes(String(a.id))) ||
            []
          : assessments?.find((a) => assessmentIds?.includes(String(a.id))) ||
            null
      }
      onChange={(_, newValue) => {
        if (multiple) {
          setValue(
            (newValue as Assessment[]).map((v) => String(v.id)).join(',')
          );
        } else {
          setValue(String((newValue as Assessment)?.id || ''));
        }
      }}
      options={assessments}
      getOptionLabel={(option: Assessment) => option.name}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Select Assessment" />
      )}
    />
  );
};
